// ============================================================================
// Invoicing module — local types and constants. Phase 1.5 (2026-06-07).
// DB tables: invoices, invoice_line_items. Source of unbilled hours:
// client_billing_lines (hours IS NOT NULL, invoice_id IS NULL). Rates come
// from the facility's Active contract line items (see create_invoice_draft_v2).
// ============================================================================

export type InvoiceStatus =
  | 'Draft'
  | 'Pending_Hours_Validation'
  | 'Pending_Finance_Review'
  | 'Approved'
  | 'Sent'
  | 'Paid'
  | 'Overdue'
  | 'Voided'
  | 'Disputed';

export type InvoiceBillingModel = 'Monthly' | 'Hourly' | 'Project_Only' | 'Hybrid';

export type InvoiceLineType =
  | 'Monthly_Service'
  | 'Hours_Block'
  | 'Project'
  | 'Adjustment'
  | 'Other';

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  Draft: 'Draft',
  Pending_Hours_Validation: 'Pending Hours Validation',
  Pending_Finance_Review: 'Pending Finance Review',
  Approved: 'Approved',
  Sent: 'Sent',
  Paid: 'Paid',
  Overdue: 'Overdue',
  Voided: 'Voided',
  Disputed: 'Disputed',
};

export const INVOICE_STATUS_CLASSES: Record<InvoiceStatus, string> = {
  Draft: 'bg-tcs-surface text-tcs-text-secondary',
  Pending_Hours_Validation: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Pending_Finance_Review: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Approved: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Sent: 'bg-indigo-100 text-indigo-800',
  Paid: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  Overdue: 'bg-st-danger-bg-strong text-st-danger-fg',
  Voided: 'bg-tcs-border text-tcs-text-secondary',
  Disputed: 'bg-st-danger-bg-strong text-st-danger-fg',
};

// ----- Row shapes (denormalized from PostgREST embedded selects) ------------

export interface InvoiceRow {
  invoice_id: string;
  invoice_number: string | null;
  account_id: string;
  facility_id: string | null;
  billing_period_start: string;
  billing_period_end: string;
  billing_model: InvoiceBillingModel;
  monthly_rate_amount: number | null;
  total_hours: number | null;
  hourly_rate: number | null;
  hours_subtotal: number | null;
  project_subtotal: number | null;
  adjustments: number | null;
  subtotal: number | null;
  total_amount: number | null;
  status: InvoiceStatus;
  due_date: string | null;
  sent_at: string | null;
  paid_at: string | null;
  paid_amount: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Embedded joins
  facilities?: { facility_name: string | null; facility_type: string | null } | null;
  accounts?: { account_name: string | null } | null;
}

export interface InvoiceLineItemRow {
  line_id: string;
  invoice_id: string;
  line_type: InvoiceLineType;
  description: string;
  source_project_id: string | null;
  source_billing_line_id: string | null;
  hours_quantity: number | null;
  unit_rate: number | null;
  line_amount: number;
  notes: string | null;
  created_at: string;
}

// ----- Queue (unbilled client_billing_lines, grouped by facility) -----------

export interface QueueDept {
  dept: string;
  lines: number;
  hours: number;
}

export interface QueueFacility {
  facility_id: string;
  facility_name: string;
  facility_type: string | null;
  account_id: string | null;
  account_name: string | null;
  depts: QueueDept[];
  total_lines: number;
  total_hours: number;
  earliest_week: string | null;
  latest_week: string | null;
  has_active_contract: boolean;
}

// ----- Ready to invoice (AFB-0035) — derived from v_ready_to_invoice --------
// One row per Active, started, not-yet-billed-this-month facility. See the
// migration 20260629_v_ready_to_invoice.sql for the column contract.
export interface ReadyToInvoiceRow {
  facility_id: string;
  facility_name: string | null;
  facility_type: string | null;
  service_account_id: string | null;
  service_account_name: string | null;
  bill_to_account_id: string | null;
  bill_to_account_name: string | null;
  main_cleaner_name: string | null;
  billing_model: string | null;
  monthly_rate: number | null;
  effective_start: string | null;
  ready_override: string | null;
  last_invoice_id: string | null;
  last_invoiced_period_end: string | null;
  last_invoiced_at: string | null;
  account_manager_eid: string | null;
}

// ----- Contract rate lines available to map at draft time -------------------

export interface ContractRateLine {
  line_id: string;
  contract_id: string;
  description: string;
  line_type: string; // Hourly_Position | Recurring_Monthly | ...
  unit_rate: number | null;
  unit_label: string | null;
}

// One row of the dept -> approved-contract-line mapping the user builds.
export interface DeptRateMapEntry {
  dept: string;
  contract_line_id: string;
}

// Money with cents — invoice amounts need precision the shared whole-dollar
// fmtCurrency drops. Returns '—' for null so callers can render unconditionally.
export function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fmtHours(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
}
